import { CloudDownloadOutlined, QuestionCircleOutlined, TeamOutlined } from '@ant-design/icons';
import { Button, Card, List, Space, Typography, Badge, Grid, Tabs, Tooltip } from 'antd';
import { Content } from 'antd/es/layout/layout';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../App';
import { AddClinicalTrialPopup, ClinicalTrialPopup } from '../components/ClinicalTrialPopup';
import { PatientAdmissionsTable } from '../components/PatientAdmission';
import { PatientsTable } from '../components/Patients';
import { Ecrf, Inquiry, Patient, PatientAdmission, UserInfo } from '../models/models';
import { deletePatient, getECRFS, getMyInquiries, getPatientAdmission, getPatients, postPatient, postPatientAdmission } from '../models/serverApi';
import EcrfList from '../components/EcrfList';
import dayjs from 'dayjs';

const Dashboard: React.FC = props => {
    const [context, setContext] = useContext(AppContext);
    const [ready, setReady] = useState(false);
    const [users, setUsers] = useState<Array<UserInfo>>([]);
    const [patientAdmissions, setPatientAdmissions] = useState<Array<PatientAdmission>>([]);
    const [patients, setPatients] = useState<Array<Patient>>([]);
    const [pendingInquiries, setPendingInquiries] = useState<Inquiry[]>([]);

    const navigate = useNavigate();
    const screens = Grid.useBreakpoint();

    useEffect(() => {
        if (ready) {
            return;
        }

        getPatientAdmission().then((pa) => {
            setPatientAdmissions(pa);
            setReady(true);
        });

        getPatients().then((pa) => {
            setPatients(pa);
            setReady(true);
        });

        getMyInquiries().then(setPendingInquiries);

        setContext(prevContext => {
            return {
                ...prevContext,
                selection: {
                    clinicalTrial: prevContext.selection?.clinicalTrial,
                    patient: undefined,
                    admission: undefined,
                }
            };
        });
    }, [ready]);

    // Marks the page for the dashboard-only background texture in App.css.
    // Set on <body> rather than a wrapper because the element that paints it -
    // .dashboard-layout - is rendered by DashboardLayout, above this component.
    // Cleaned up on unmount so the texture does not follow the user into the
    // CRF, where it would sit under dense form content.
    useEffect(() => {
        document.body.setAttribute("data-page", "dashboard");
        return () => document.body.removeAttribute("data-page");
    }, []);

    const isAdmin = () => {
        return context.user?.admin;
    }

    const patientAdmissionFinish = async (patientAdmission: PatientAdmission) => {
        setReady(false);
    }

    const patientAdmissionDeleteFinish = (patientAdmission: PatientAdmission) => {
        setReady(false);
    }

    const patientsFinish = async (patient?: Patient) => {
        setReady(false);
    }

    const getFilesSource = () => {
        // The trial is only in context after an eCRF has been opened, so on a fresh
        // login there is none. Fall back to the user's first trial rather than
        // returning nothing, which left the study-material links absent entirely.
        const trial = context.selection?.clinicalTrial
            ?? context.ecrfs?.[0]?.trial;

        if (!trial) {
            return [];
        }

        return [{
            name: "Πρωτόκολλο μελέτης",
            file: trial.protocolFile,
        },
        {
            name: "Φόρμα συγκατάθεσης ασθενούς",
            file: trial.patientConsentFile
        }];
    }

    const handleInquiryClick = async (inquiry: Inquiry) => {
        const [pts, admissions] = await Promise.all([getPatients(), getPatientAdmission()]);
        const patient = pts.find(p => p.id === inquiry.patientId);
        const admission = admissions.find(a => a.id === inquiry.patientAdmissionId);
        setContext(prev => ({
            ...prev,
            selection: { ...prev.selection, patient, admission } as any
        }));
        navigate(
            `/ecrf/${inquiry.ecrfId}?sectionId=${inquiry.sectionId}&fieldId=${inquiry.fieldId}`,
            { state: { patientId: inquiry.patientId, admissionId: inquiry.patientAdmissionId } }
        );
    };

    return (<>
        <Badge.Ribbon text={context.user?.admin ? "Admin" : context.user?.professor ? "Professor" : "User"}>
            <Content>
                {/* Tabs rather than side-by-side cards: the patient list is the
                   purpose of this page and now gets the full width, instead of
                   surrendering a third of it permanently to a queue that is
                   usually short. The queue is not hidden by this - its count is
                   badged onto the tab header, so it is visible on arrival from
                   whichever tab is open, and unread work still announces itself.
                   The two study PDFs live in tabBarExtraContent so they stay
                   reachable from both tabs without costing a tab of their own -
                   they are read once during training. */}
                <Tabs
                    defaultActiveKey="patients"
                    style={{ marginTop: 24 }}
                    tabBarExtraContent={{
                        right: (
                            <Space wrap>
                                {getFilesSource().map(f => (
                                    <Tooltip
                                        key={f.name}
                                        title={f.file ? undefined : "Δεν έχει αναρτηθεί αρχείο για αυτή τη μελέτη"}
                                    >
                                        {/* Rendered even without a file, but disabled: a
                                            disabled link says "this exists but is missing",
                                            where hiding it says nothing at all and the old
                                            card was worse still - an enabled button that
                                            opened /Uploads/undefined. */}
                                        <Button
                                            type="link"
                                            size="small"
                                            disabled={!f.file}
                                            icon={<CloudDownloadOutlined />}
                                            onClick={() => window.open(`/Uploads/${f.file}`, "_blank")}
                                        >
                                            {f.name}
                                        </Button>
                                    </Tooltip>
                                ))}
                            </Space>
                        )
                    }}
                    items={[
                        {
                            key: "patients",
                            label: <span><TeamOutlined /> Ασθενείς</span>,
                            children: (
                                <PatientsTable patients={patients} patientAdmissions={patientAdmissions} afterEdit={patientsFinish} afterDelete={patientsFinish} ></PatientsTable>
                            ),
                        },
                        {
                            key: "inquiries",
                            label: (
                                <span>
                                    <QuestionCircleOutlined /> Εκκρεμείς Ερωτήσεις
                                    {/* The badge is the reason tabs do not bury the
                                        queue: it is painted on the tab bar itself, so an
                                        outstanding count is on screen even while the
                                        patients tab is the active one. */}
                                    <Badge
                                        count={pendingInquiries.length}
                                        // Amber, not the default red: an open query is
                                        // routine work awaiting an answer, not an error.
                                        // Red here would cry wolf on every dashboard.
                                        color="#faad14"
                                        style={{ marginInlineStart: 8 }}
                                    />
                                </span>
                            ),
                            children: (
                                <>
                                {pendingInquiries.length === 0 ? (
                                    <Typography.Text type="secondary">Δεν υπάρχουν εκκρεμείς ερωτήσεις.</Typography.Text>
                                ) : (
                                    <List
                                        dataSource={pendingInquiries}
                                        // Paginated so a long queue does not scroll on
                                        // forever; the list was previously unbounded.
                                        // On its own full-width tab it affords more
                                        // rows and a real pager than the old rail did.
                                        pagination={pendingInquiries.length > 12
                                            ? { pageSize: 12, size: "small" }
                                            : false}
                                        renderItem={(item) => (
                                            <List.Item
                                                actions={[
                                                    // Primary again: it was demoted to fit
                                                    // the narrow rail, but on a full-width
                                                    // tab there is room, and going to the
                                                    // field is the point of the row.
                                                    <Button type="primary" size="small" onClick={() => handleInquiryClick(item)}>
                                                        Μετάβαση
                                                    </Button>
                                                ]}
                                            >
                                                <List.Item.Meta
                                                    title={
                                                        <span>
                                                            Πεδίο: <strong>{item.fieldLabel ?? `#${item.fieldId}`}</strong>
                                                            {" — "}
                                                            Ασθενής: <strong>{item.patientName ?? `#${item.patientId}`}</strong>
                                                        </span>
                                                    }
                                                    description={
                                                        <span>
                                                            Από: {item.fromUserName}
                                                            {item.sectionName && ` | Ενότητα: ${item.sectionName}`}
                                                            {item.note && ` | Σημείωση: "${item.note}"`}
                                                            {item.fieldValueSnapshot && ` | Τιμή: ${item.fieldValueSnapshot}`}
                                                            {` | ${dayjs(item.createdAt).format("DD/MM/YYYY HH:mm")}`}
                                                        </span>
                                                    }
                                                />
                                            </List.Item>
                                        )}
                                    />
                                )}
                                </>
                            ),
                        },
                    ]}
                />
                <Card bordered hoverable className="always" style={{ marginTop: "50px" }}>
                    &copy; 2026 - <a href="https://www.ede.gr/">Ελληνική Διαβητολογική Εταιρεία</a> | Powered by <a href="https://biostats.gr/e-crf-edc/">biostats.gr</a>
                </Card>
            </Content>
        </Badge.Ribbon>
    </>);
}

export default Dashboard;
